import apiClient from '../client';
import type {
  Demand,
  DemandDetail,
  DemandStats,
  DemandFilters,
  DemandInteraction,
  CreateDemandRequest,
  AssignDemandRequest,
  UpdateDemandStatusRequest,
  ResolveDemandRequest,
  AddDemandNoteRequest,
  PortalCreateDemandRequest,
  PortalDemandResponse,
} from '@/types/demand.types';
import type { PaginatedResponse, PaginationParams } from '@/api/types/pagination.types';

export const demandsApi = {
  // ============ Admin/Staff endpoints ============

  /**
   * Cria uma nova demanda (pelo staff)
   */
  create: async (request: CreateDemandRequest): Promise<DemandDetail> => {
    const response = await apiClient.post<DemandDetail>('/v1/demands', request);
    return response.data;
  },

  /**
   * Lista todas as demandas com filtros opcionais
   */
  getAll: async (
    params: PaginationParams & DemandFilters
  ): Promise<PaginatedResponse<Demand>> => {
    const response = await apiClient.get<PaginatedResponse<Demand>>('/v1/demands', { params });
    return response.data;
  },

  /**
   * Lista demandas pendentes (OPEN ou IN_PROGRESS)
   */
  getPending: async (params: PaginationParams): Promise<PaginatedResponse<Demand>> => {
    const response = await apiClient.get<PaginatedResponse<Demand>>('/v1/demands/pending', {
      params,
    });
    return response.data;
  },

  /**
   * Lista demandas não atribuídas
   */
  getUnassigned: async (params: PaginationParams): Promise<PaginatedResponse<Demand>> => {
    const response = await apiClient.get<PaginatedResponse<Demand>>('/v1/demands/unassigned', {
      params,
    });
    return response.data;
  },

  /**
   * Lista demandas atribuídas ao usuário atual
   */
  getMyDemands: async (): Promise<Demand[]> => {
    const response = await apiClient.get<Demand[]>('/v1/demands/my');
    return response.data;
  },

  /**
   * Retorna estatísticas das demandas
   */
  getStats: async (): Promise<DemandStats> => {
    const response = await apiClient.get<DemandStats>('/v1/demands/stats');
    return response.data;
  },

  /**
   * Busca demanda por ID com detalhes completos
   */
  getById: async (id: string): Promise<DemandDetail> => {
    const response = await apiClient.get<DemandDetail>(`/v1/demands/${id}`);
    return response.data;
  },

  /**
   * Atribui uma demanda a um colaborador
   */
  assign: async (id: string, request: AssignDemandRequest): Promise<DemandDetail> => {
    const response = await apiClient.post<DemandDetail>(`/v1/demands/${id}/assign`, request);
    return response.data;
  },

  /**
   * Atualiza o status de uma demanda
   */
  updateStatus: async (id: string, request: UpdateDemandStatusRequest): Promise<DemandDetail> => {
    const response = await apiClient.put<DemandDetail>(`/v1/demands/${id}/status`, request);
    return response.data;
  },

  /**
   * Resolve uma demanda
   */
  resolve: async (id: string, request: ResolveDemandRequest): Promise<DemandDetail> => {
    const response = await apiClient.post<DemandDetail>(`/v1/demands/${id}/resolve`, request);
    return response.data;
  },

  /**
   * Adiciona uma anotação a uma demanda
   */
  addNote: async (id: string, request: AddDemandNoteRequest): Promise<DemandInteraction> => {
    const response = await apiClient.post<DemandInteraction>(`/v1/demands/${id}/notes`, request);
    return response.data;
  },

  /**
   * Aceita uma demanda (atribui ao usuario atual)
   */
  accept: async (id: string): Promise<DemandDetail> => {
    const response = await apiClient.post<DemandDetail>(`/v1/demands/${id}/accept`);
    return response.data;
  },

  /**
   * Cancela uma demanda em aberto
   */
  cancel: async (id: string, reason?: string): Promise<DemandDetail> => {
    const response = await apiClient.post<DemandDetail>(`/v1/demands/${id}/cancel`, null, {
      params: { reason },
    });
    return response.data;
  },

  /**
   * Lista demandas na fila (pendentes ordenadas por urgencia)
   */
  getQueue: async (params?: PaginationParams): Promise<PaginatedResponse<Demand>> => {
    const response = await apiClient.get<PaginatedResponse<Demand>>('/v1/demands/pending', {
      params,
    });
    return response.data;
  },

  /**
   * Lista demandas de um paciente especifico
   */
  getByPatient: async (patientId: string): Promise<Demand[]> => {
    const response = await apiClient.get<Demand[]>(`/v1/demands/patient/${patientId}`);
    return response.data;
  },

  // ============ Portal (Patient) endpoints ============

  /**
   * Cria uma nova demanda (portal do paciente)
   */
  portalCreate: async (
    accessToken: string,
    request: PortalCreateDemandRequest
  ): Promise<PortalDemandResponse> => {
    const response = await apiClient.post<PortalDemandResponse>('/v1/portal/demands', request, {
      headers: { 'X-Portal-Token': accessToken },
    });
    return response.data;
  },

  /**
   * Lista demandas do paciente (portal)
   */
  portalGetAll: async (
    accessToken: string,
    params: PaginationParams
  ): Promise<PaginatedResponse<PortalDemandResponse>> => {
    const response = await apiClient.get<PaginatedResponse<PortalDemandResponse>>(
      '/v1/portal/demands',
      {
        params,
        headers: { 'X-Portal-Token': accessToken },
      }
    );
    return response.data;
  },

  /**
   * Lista demandas em aberto do paciente (portal)
   */
  portalGetOpen: async (accessToken: string): Promise<PortalDemandResponse[]> => {
    const response = await apiClient.get<PortalDemandResponse[]>('/v1/portal/demands/open', {
      headers: { 'X-Portal-Token': accessToken },
    });
    return response.data;
  },

  /**
   * Busca uma demanda específica pelo ID (portal)
   */
  portalGetById: async (accessToken: string, id: string): Promise<PortalDemandResponse> => {
    const response = await apiClient.get<PortalDemandResponse>(`/v1/portal/demands/${id}`, {
      headers: { 'X-Portal-Token': accessToken },
    });
    return response.data;
  },

  /**
   * Cancela uma demanda em aberto (portal)
   */
  portalCancel: async (accessToken: string, id: string): Promise<void> => {
    await apiClient.delete(`/v1/portal/demands/${id}`, {
      headers: { 'X-Portal-Token': accessToken },
    });
  },
};
